import React, { useState, useEffect, useRef } from 'react';
import 'src/style/stagedashboard.css';
import StageRightSidebarV2 from 'src/components/StageV2/StageRightSidebarV2';
import usePageTitle from 'src/utils/SetPageTitle';
import { useDispatch, useSelector } from 'react-redux';
import {
  getStageData,
  getAssignedStages,
  getWorkflowStagesForVehicleTracking,
  clearWorkflowStagesForVehicleTracking,
  getAssignedPlantForEmployee,
} from 'src/redux/Actions/VehicleTracking';
import StageDashboardMainContentV2 from 'src/components/StageV2/StageDashboardMainContentV2';
import { decodeUuidFromBase64 } from 'src/utils/DecodeBase64Function';
import { Col, Row, Modal } from 'react-bootstrap';
import { ReactComponent as ArrowRight } from 'src/Images/icons/common/arrowright.svg';
import getColorCode from 'src/utils/GetThemeColor';
import Drawer from '@mui/material/Drawer';
import { useMediaQuery } from '@mui/system';
import CustomMultiSelect from 'src/utils/CustomMultiSelect';
import Breadcrumb from 'src/components/Breadcrumb';
import { useSetBreadcrumbData } from 'src/utils/SetBreadcrumbData';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Alert } from '@mui/material';
import { CLEAR_API_ERROR, CLEAR_STAGE_DATA } from 'src/redux/Actions/Type';
import { usePermissions } from 'src/utils/hooks/usePermissions';
import { ACTIONS } from 'src/utils/roles/actions';
import { getCache, setCache } from 'src/utils/cache';

const STAGE_DASHBOARD_DIRECTION_FILTER_CACHE_KEY =
  'stageDashboardDirectionFilters';

export default function StageDashboard() {
  usePageTitle(`Stage Dashboard`);
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { workFlowStageName, workFlowStageUniqueNumber } = useParams();
  const vehicleTrackingDataReducer = useSelector(
    (state) => state.VehicleTrackingReducer,
  );
  const assignedStagesData = vehicleTrackingDataReducer.assignedStages || [];
  const isMobile = useMediaQuery('(max-width:600px)');
  const authReducer = useSelector((state) => state.AuthReducer);
  const [selectedStage, setSelectedStage] = useState({
    id: null,
    uniqueNumber: null,
  });
  const organisationId = authReducer.userData.organisations?.id;
  const profileId = authReducer.userData?.profile_id;
  const themeColor = authReducer?.userData?.tenant?.theme;
  const [showV2RightSidebar, setShowV2RightSidebar] = useState(false);
  const [selectedPlant, setSelectedPlant] = useState(null);

  // Use centralized permission hook
  const { isPermissionsLoaded, hasPermission } = usePermissions();

  // Compute permission flags
  const hasInwardPermission =
    hasPermission(ACTIONS.VEHICLE_TRACKING.INWARD.VIEW) ||
    hasPermission(ACTIONS.VEHICLE_TRACKING.INWARD.CREATE);
  const hasOutwardPermission =
    hasPermission(ACTIONS.VEHICLE_TRACKING.OUTWARD.VIEW) ||
    hasPermission(ACTIONS.VEHICLE_TRACKING.OUTWARD.CREATE);

  const [selectedDirection, setSelectedDirection] = useState([]);

  const [
    showVehicleTrackingNotFoundAlert,
    setShowVehicleTrackingNotFoundAlert,
  ] = useState({
    status: false,
    errorMsg: '',
  });

  const isSubmittingGetStagesButtonRef = useRef(false);

  const errorMessages = useSelector(
    (state) => state.GlobalErrorReducer.errorMessages,
  );

  const assignedPlantData =
    vehicleTrackingDataReducer.assignedPlantForEmployee || [];

  // Set initial direction based on permissions once they are loaded
  useEffect(() => {
    if (isPermissionsLoaded && selectedDirection.length === 0) {
      const permittedDirections = [];

      if (hasInwardPermission) permittedDirections.push(1);
      if (hasOutwardPermission) permittedDirections.push(2);

      const cachedDirection = getCache(
        STAGE_DASHBOARD_DIRECTION_FILTER_CACHE_KEY,
      );
      const cachedDirectionArray = Array.isArray(cachedDirection)
        ? cachedDirection
        : [];
      const cachedPermittedDirection = cachedDirectionArray.filter(
        (direction) => permittedDirections.includes(direction),
      );
      setSelectedDirection(
        cachedPermittedDirection.length
          ? cachedPermittedDirection
          : permittedDirections.length
            ? permittedDirections
            : [1],
      );
    }
  }, [
    hasInwardPermission,
    hasOutwardPermission,
    isPermissionsLoaded,
    selectedDirection.length,
  ]);

  useEffect(() => {
    if (organisationId) {
      dispatch(getAssignedPlantForEmployee(organisationId, '')).then((data) => {
        const selectedPlantOptions =
          data?.map((plant) => ({
            label: plant.name,
            value: plant.id,
          })) || [];
        setSelectedPlant(selectedPlantOptions);
      });
    } else {
      setSelectedPlant([]);
    }
  }, [dispatch, organisationId]);

  // this useEffect handles showing alert if user is redirected from vehicle tracking detail page due to not found error
  useEffect(() => {
    const vehicleNotFoundRedirectFromDetail = errorMessages.find(
      (err) => err.id === 'VEHICLE_TRACKING_NOT_FOUND',
    );
    if (vehicleNotFoundRedirectFromDetail) {
      setShowVehicleTrackingNotFoundAlert({
        status: true,
        errorMsg:
          vehicleNotFoundRedirectFromDetail.message ||
          'Vehicle tracking not found',
      });
    }
  }, [errorMessages]);

  const plantOptions = assignedPlantData.map((plant) => ({
    label: plant.name,
    value: plant.id,
  }));

  useEffect(() => {
    if (isPermissionsLoaded && profileId && selectedDirection.length > 0) {
      dispatch(getAssignedStages(profileId, selectedDirection));
    }
  }, [dispatch, profileId, selectedDirection, isPermissionsLoaded]);

  const convertToLowerCase = (inputText) =>
    inputText
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');

  useEffect(() => {
    if (!assignedStagesData.length) return;
    const stageToSelect =
      assignedStagesData.find(
        (stage) =>
          workFlowStageName &&
          workFlowStageUniqueNumber &&
          convertToLowerCase(stage.name) === workFlowStageName &&
          stage.vehicle_tracking_stage_unique_number ===
            workFlowStageUniqueNumber,
      ) || assignedStagesData[0];
    setSelectedStage({
      id: stageToSelect.id,
      uniqueNumber: stageToSelect.vehicle_tracking_stage_unique_number,
    });
  }, [workFlowStageName, workFlowStageUniqueNumber, assignedStagesData]);

  // Handle user manually selecting a stage from the dropdown
  const handleStageChange = (selectedOption) => {
    if (!selectedOption) return; // Ignore if no option selected
    // Find the full stage object based on selected stage ID
    const stage = assignedStagesData.find((s) => s.id === selectedOption.value);
    if (!stage) return;
    // Update local state with the new stage selection
    setSelectedStage({
      id: stage.id,
      uniqueNumber: stage.vehicle_tracking_stage_unique_number,
    });
    navigate(
      `/stations/${convertToLowerCase(stage.name)}/${stage.vehicle_tracking_stage_unique_number}`,
    );
  };

  const handleDirectionChange = (selectedOption) => {
    const selectedDirectionValues =
      selectedOption?.map((option) => option.value) || [];
    if (selectedDirectionValues.length === 0) return;

    dispatch({ type: CLEAR_STAGE_DATA });
    setSelectedStage({ id: null, uniqueNumber: null });
    setSelectedDirection(selectedDirectionValues);
    setCache(
      STAGE_DASHBOARD_DIRECTION_FILTER_CACHE_KEY,
      selectedDirectionValues,
    );
  };

  const handlePlantChange = (selectedOptions) => {
    setSelectedPlant(selectedOptions || []);
    dispatch({ type: CLEAR_STAGE_DATA });
  };

  useEffect(() => {
    if (
      selectedStage.id &&
      organisationId &&
      selectedDirection.length > 0 &&
      selectedPlant !== null
    ) {
      const selectedPlantIds = selectedPlant.map((plant) => plant.value);

      dispatch(
        getStageData(
          selectedStage.id,
          organisationId,
          selectedDirection,
          selectedPlantIds,
        ),
      );
    }
  }, [
    dispatch,
    selectedStage.id,
    organisationId,
    selectedDirection,
    selectedPlant,
  ]);

  const fetchAndHandleWorkflowStages = async ({ stageId, inputId }) =>
    dispatch(getWorkflowStagesForVehicleTracking(stageId, inputId))
      .then((response) => {
        // release lock ONLY after API finishes
        isSubmittingGetStagesButtonRef.current = false;
        const { workflowStages } = response;
        if (workflowStages && workflowStages.length === 1) {
          navigate(`/${workflowStages[0].url}`);
          dispatch(clearWorkflowStagesForVehicleTracking());
        }
      })
      .catch((error) => {
        isSubmittingGetStagesButtonRef.current = false; // UNLOCK ON ERROR
        throw error; // let caller decide how to show error
      });

  const handleNavigation = async (inputData) => {
    const decodedId = decodeUuidFromBase64(inputData);
    const inputId = decodedId || inputData;

    if (
      inputId !== null &&
      inputId !== '' &&
      !isSubmittingGetStagesButtonRef.current
    ) {
      isSubmittingGetStagesButtonRef.current = true; // SET LOCK FIRST
      fetchAndHandleWorkflowStages({
        stageId: selectedStage.id,
        inputId,
      })
        .catch((error) => {
          setShowVehicleTrackingNotFoundAlert({
            status: true,
            errorMsg: error?.response?.data?.error,
          });
        })
        .finally(() => {
          isSubmittingGetStagesButtonRef.current = false;
        });
    }
  };

  useEffect(() => {
    // Declare a buffer to store keypresses and a timeout variable
    let buffer = '',
      timeout;
    const handleKeyPress = (event) => {
      // HARD LOCK: block while API is running
      if (isSubmittingGetStagesButtonRef.current) return;
      // Clear any existing timeout to reset the buffer clearing delay
      if (timeout) clearTimeout(timeout);
      if (event.key === 'Enter') {
        if (buffer) {
          // Remove any occurrences of 'Shift' from the buffer
          buffer = buffer.replace(/Shift/g, '');
          const decodedId = decodeUuidFromBase64(buffer);
          if (decodedId) {
            handleNavigation(decodedId);
          }
          // Reset the buffer after processing
          buffer = '';
        }
      } else {
        // Append the pressed key to the buffer
        buffer += event.key;
      }
      timeout = setTimeout(() => {
        buffer = '';
      }, 500);
    };
    // Attach the event listener for keydown event
    window.addEventListener('keydown', handleKeyPress);
    // Cleanup function to remove the event listener
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [dispatch, selectedStage]);

  const toggleRightSidebarTable = (forceState) => {
    if (typeof forceState === 'boolean') {
      setShowV2RightSidebar(forceState);
    } else {
      setShowV2RightSidebar((prev) => !prev);
    }
  };

  const assignedStageOptions = assignedStagesData.map((stage) => ({
    label: stage.name,
    value: stage.id,
  }));

  const selectedStageOption = assignedStageOptions.find(
    (option) => option.value === selectedStage.id,
  );

  const allDirectionOptions = [
    { label: 'Inward', value: 1 },
    { label: 'Outward', value: 2 },
  ];

  // Filter direction options based on user permissions
  const directionOptions = allDirectionOptions.filter((option) => {
    if (option.value === 1) return hasInwardPermission;
    if (option.value === 2) return hasOutwardPermission;
    return false;
  });

  const selectedDirectionOption = directionOptions?.filter((option) =>
    selectedDirection.includes(option.value),
  );

  useSetBreadcrumbData(location.pathname, 'Stage Dashboard');

  return (
    <div className="page-layout">
      {/* Header */}
      <div className="div-flex-column rg-10">
        <div className="div-space-between left-padding">
          <div className="div-flex-row cg-5">
            <Breadcrumb />
          </div>
        </div>
        <Row className="border-bottom left-padding right-padding pb-2 div-space-between align-items-center">
          {!isMobile && (
            <Col xxl={6} xl={6} lg={6} md={6} sm={6} xs={6}>
              <span className="text-xxl text-bold left-padding">
                {selectedStageOption
                  ? selectedStageOption.label
                  : 'Select Station'}
              </span>
            </Col>
          )}
          <Col xxl={4} xl={4} lg={4} md={5} sm={12} xs={12}>
            <div className="div-flex-row cg-10">
              {directionOptions.length > 0 && (
                <div className="station-header div-flex-column">
                  {!isMobile && (
                    <span className="text-sm text-grey text-semi-bold">
                      Select direction
                    </span>
                  )}
                  <div className="div-flex-row">
                    <div className="input-wrapper position-relative">
                      <CustomMultiSelect
                        isMulti
                        name="selectedDirection"
                        className="station-select-input-section"
                        classNamePrefix="react-select"
                        placeholder="Select Direction"
                        options={directionOptions}
                        value={selectedDirectionOption}
                        onChange={handleDirectionChange}
                        onMenuClose={() => {
                          setTimeout(() => document.activeElement.blur(), 50);
                        }}
                      />
                      <span className="form-input-section-icon">
                        <ArrowRight
                          className="icon-size-sm"
                          color={getColorCode(themeColor)}
                          style={{ transform: 'rotate(90deg)' }}
                        />
                      </span>
                    </div>
                  </div>
                </div>
              )}
              <div className="station-header div-flex-column">
                {!isMobile && (
                  <span className="text-sm text-grey text-semi-bold">
                    Select plant
                  </span>
                )}
                <div className="div-flex-row">
                  <div className="input-wrapper position-relative">
                    <CustomMultiSelect
                      isMulti
                      name="selectedPlant"
                      className="station-select-input-section"
                      classNamePrefix="react-select"
                      placeholder="Select Plant"
                      options={plantOptions}
                      value={selectedPlant || []}
                      onChange={handlePlantChange}
                      onMenuClose={() => {
                        setTimeout(() => document.activeElement.blur(), 50);
                      }}
                    />
                    <span className="form-input-section-icon">
                      <ArrowRight
                        className="icon-size-sm"
                        color={getColorCode(themeColor)}
                        style={{ transform: 'rotate(90deg)' }}
                      />
                    </span>
                  </div>
                </div>
              </div>
              <div className="station-header div-flex-column">
                {!isMobile && (
                  <span className="text-sm text-grey text-semi-bold">
                    Select another station
                  </span>
                )}
                <div className="div-flex-row">
                  <div className="input-wrapper position-relative">
                    <CustomMultiSelect
                      isMulti={false}
                      name="selectedStage"
                      className="station-select-input-section"
                      classNamePrefix="react-select"
                      placeholder="Select Station"
                      options={assignedStageOptions}
                      value={selectedStageOption}
                      onChange={handleStageChange}
                      onMenuClose={() => {
                        setTimeout(() => document.activeElement.blur(), 50);
                      }}
                    />
                    <span className="form-input-section-icon">
                      <ArrowRight
                        className="icon-size-sm"
                        color={getColorCode(themeColor)}
                        style={{ transform: 'rotate(90deg)' }}
                      />
                    </span>
                  </div>
                  <Drawer
                    anchor="right"
                    open={Boolean(showV2RightSidebar)}
                    onClose={() => toggleRightSidebarTable(false)}
                    ModalProps={{
                      keepMounted: false,
                    }}
                    sx={{ zIndex: 2 }}
                  >
                    <div className="right-drawer-component-medium">
                      <StageRightSidebarV2
                        toggleRightSidebarTable={toggleRightSidebarTable}
                        handleNavigation={handleNavigation}
                      />
                    </div>
                  </Drawer>
                </div>
              </div>
              {isMobile && (
                <Col xxl={2} xl={2} lg={2}>
                  <button
                    className={`header-button theme-background-${themeColor}`}
                    onClick={() => toggleRightSidebarTable()}
                  >
                    <span className="text-white">Scan</span>
                  </button>
                </Col>
              )}
            </div>
          </Col>
        </Row>
      </div>
      <div>
        <StageDashboardMainContentV2
          showRightSidebar={showV2RightSidebar}
          toggleRightSidebarTable={toggleRightSidebarTable}
        />
        {showV2RightSidebar && (
          <StageRightSidebarV2
            toggleRightSidebarTable={toggleRightSidebarTable}
            handleNavigation={handleNavigation}
          />
        )}
      </div>
      <Modal
        show={showVehicleTrackingNotFoundAlert.status}
        onHide={() => {
          setShowVehicleTrackingNotFoundAlert({ status: false, errorMsg: '' });
          dispatch({
            type: CLEAR_API_ERROR,
            payload: { id: 'VEHICLE_TRACKING_NOT_FOUND' },
          });
        }}
      >
        <Alert severity="error">
          {showVehicleTrackingNotFoundAlert?.errorMsg}
        </Alert>
      </Modal>
    </div>
  );
}
